import { z } from 'zod';

import {
  ShipmentBuilderInputSchema,
  ShipmentTotalsSummarySchema,
} from '../../shipments/shipments-shared.schema.js';
import { CounterpartySnapshotSchema } from '../../counterparties/counterparties.schema.js';
import {
  IsoDateTimeSchema,
  PresentationTypeSchema,
  RcScopeTypeSchema,
  ShipmentScaleTypeSchema,
} from '../../shipments/shipments-types.js';
import { ShipmentLogisticsCaptureShellMaterialRefSchema } from '../../shipments/shipment-logistics-capture-shell.schema.js';
import {
  ShipmentDriverAssignmentSchema,
  ShipmentWeighEventSchema,
} from '../../shipments/shipment-weigh-evidence.schema.js';
import { OutboundRecordTypeSchema } from '../../outbound/outbound-record-reference.schema.js';
import type { OutboundRecordType } from '../../outbound/outbound-record-reference.dto.js';
import { shipmentVoidKindValues } from './shipments-sync.dto.js';
import type {
  ShipmentDriverAssignmentSyncPayload,
  ShipmentDraftPayload,
  ShipmentLogisticsShellReversedEventPayload,
  ShipmentLogisticsShellUpsertedEventPayload,
  ShipmentSavedEventPayload,
  ShipmentSavedLineItemPayload,
  ShipmentScaleTicketEventPayload,
  ShipmentScaleTicketPayload,
  ShipmentWeighEventSyncPayload,
  ShipmentVoidedEventPayload,
} from './shipments-sync.dto.js';

const nonNegativeNumberSchema = z.number().min(0);
const validateScaleTicketPayloadWeights = (value: unknown, ctx: z.RefinementCtx): void => {
  if (!value || typeof value !== 'object') {
    return;
  }

  const gross = (value as { grossWeightLb?: number | null }).grossWeightLb;
  const tare = (value as { tareWeightLb?: number | null }).tareWeightLb;
  const net = (value as { netWeightLb?: number | null }).netWeightLb;
  const isComplete = (value as { isComplete?: boolean }).isComplete;

  if (typeof tare === 'number' && (typeof gross !== 'number' || gross < tare)) {
    ctx.addIssue({
      code: 'custom',
      message: 'grossWeightLb must be provided and >= tareWeightLb when tareWeightLb exists.',
      path: ['grossWeightLb'],
    });
    return;
  }

  if (typeof gross === 'number' && typeof tare === 'number' && typeof net === 'number') {
    const expectedNet = gross - tare;
    if (Math.abs(net - expectedNet) > 1e-9) {
      ctx.addIssue({
        code: 'custom',
        message: 'netWeightLb must equal grossWeightLb - tareWeightLb when both are present.',
        path: ['netWeightLb'],
      });
    }
  }

  if (typeof isComplete === 'boolean') {
    const hasNet = typeof net === 'number' && !Number.isNaN(net);
    if (isComplete !== hasNet) {
      ctx.addIssue({
        code: 'custom',
        message: 'isComplete must match netWeightLb presence.',
        path: ['isComplete'],
      });
    }
  }
};

const ShipmentSyncPayloadBaseSchema = z
  .object({
    schemaVersion: z.literal(1),
    eventId: z.uuid(),
    occurredAt: IsoDateTimeSchema,
    companyId: z.uuid(),
    stationId: z.uuid(),
    shipmentId: z.uuid(),
  })
  .strict();

const ShipmentLogisticsShellPayloadBaseFields = {
  schemaVersion: z.literal(1),
  eventId: z.uuid(),
  occurredAt: IsoDateTimeSchema,
  companyId: z.uuid(),
  stationId: z.uuid(),
  shipmentId: z.uuid().nullable().optional(),
  outboundRecordType: OutboundRecordTypeSchema.optional(),
  outboundRecordId: z.uuid().optional(),
} as const;

function normalizeShipmentLogisticsShellReference<
  T extends {
    shipmentId?: string | null;
    outboundRecordType?: OutboundRecordType;
    outboundRecordId?: string;
  },
>(
  value: T,
  ctx: z.RefinementCtx,
): T & {
  shipmentId: string | null;
  outboundRecordType: OutboundRecordType;
  outboundRecordId: string;
} {
  const outboundRecordType = value.outboundRecordType ?? 'SHIPMENT';
  const outboundRecordId = value.outboundRecordId ?? value.shipmentId;
  if (!outboundRecordId) {
    ctx.addIssue({
      code: 'custom',
      message: 'outboundRecordId is required when shipmentId is not provided.',
      path: ['outboundRecordId'],
    });
    return z.NEVER;
  }

  if (outboundRecordType === 'SHIPMENT') {
    if (value.shipmentId && value.shipmentId !== outboundRecordId) {
      ctx.addIssue({
        code: 'custom',
        message: 'shipmentId must match outboundRecordId for SHIPMENT shell payloads.',
        path: ['shipmentId'],
      });
      return z.NEVER;
    }

    return {
      ...value,
      shipmentId: value.shipmentId ?? outboundRecordId,
      outboundRecordType,
      outboundRecordId,
    };
  }

  if (value.shipmentId !== undefined && value.shipmentId !== null) {
    ctx.addIssue({
      code: 'custom',
      message: 'shipmentId must be null or omitted for COVER_SHIPMENT shell payloads.',
      path: ['shipmentId'],
    });
    return z.NEVER;
  }

  return {
    ...value,
    shipmentId: null,
    outboundRecordType,
    outboundRecordId,
  };
}

export const ShipmentDraftEventPayloadSchema: z.ZodType<ShipmentDraftPayload> =
  ShipmentSyncPayloadBaseSchema.extend({
    builderInput: ShipmentBuilderInputSchema,
    actorUserId: z.uuid().nullable(),
    createdByUserId: z.uuid().nullable().optional(),
    shipper: CounterpartySnapshotSchema,
    receiver: CounterpartySnapshotSchema,
    sourceLoadSessionId: z.uuid().nullable().optional(),
    sourceLoadSessionGroupId: z.uuid().nullable().optional(),
  })
    .strict()
    .superRefine((value, ctx) => {
      const hasLoadSessionId =
        value.sourceLoadSessionId !== undefined && value.sourceLoadSessionId !== null;
      const hasGroupId =
        value.sourceLoadSessionGroupId !== undefined && value.sourceLoadSessionGroupId !== null;
      if (hasLoadSessionId !== hasGroupId) {
        ctx.addIssue({
          code: 'custom',
          message: 'sourceLoadSessionId and sourceLoadSessionGroupId must be provided together.',
          path: [hasLoadSessionId ? 'sourceLoadSessionGroupId' : 'sourceLoadSessionId'],
        });
      }
    });

export const ShipmentSavedLineItemPayloadSchema: z.ZodType<ShipmentSavedLineItemPayload> = z
  .object({
    lineItemId: z.uuid(),
    purchaseCommittedId: z.uuid(),
    materialId: z.uuid(),
    presentationType: PresentationTypeSchema,
    netWeightLbs: nonNegativeNumberSchema,
  })
  .strict();

export const ShipmentSavedEventPayloadSchema: z.ZodType<ShipmentSavedEventPayload> =
  ShipmentSyncPayloadBaseSchema.extend({
    actorUserId: z.uuid().nullable(),
    builderInput: ShipmentBuilderInputSchema,
    shipper: CounterpartySnapshotSchema,
    receiver: CounterpartySnapshotSchema,
    sourcePreparationRequestId: z.uuid().nullable().optional(),
    sourceLoadSessionId: z.uuid().nullable().optional(),
    sourceLoadSessionGroupId: z.uuid().nullable().optional(),
    departedAt: IsoDateTimeSchema.nullable(),
    lineItems: z.array(ShipmentSavedLineItemPayloadSchema).nonempty(),
    totals: ShipmentTotalsSummarySchema,
  })
    .strict()
    .superRefine((value, ctx) => {
      const hasLoadSessionId =
        value.sourceLoadSessionId !== undefined && value.sourceLoadSessionId !== null;
      const hasGroupId =
        value.sourceLoadSessionGroupId !== undefined && value.sourceLoadSessionGroupId !== null;
      if (hasLoadSessionId !== hasGroupId) {
        ctx.addIssue({
          code: 'custom',
          message: 'sourceLoadSessionId and sourceLoadSessionGroupId must be provided together.',
          path: [hasLoadSessionId ? 'sourceLoadSessionGroupId' : 'sourceLoadSessionId'],
        });
      }
    });

export const ShipmentVoidedEventPayloadSchema: z.ZodType<ShipmentVoidedEventPayload> =
  ShipmentSyncPayloadBaseSchema.extend({
    actorUserId: z.uuid().nullable(),
    reason: z.string().trim().min(1),
    reasonCode: z.string().trim().min(1).max(64).nullable().optional(),
    voidKind: z.enum(shipmentVoidKindValues).optional(),
  }).strict();

export const ShipmentScaleTicketPayloadSchema: z.ZodType<ShipmentScaleTicketPayload> = z
  .object({
    id: z.uuid(),
    scaleType: ShipmentScaleTypeSchema,
    ticketNumber: z.string().trim().min(1),
    weighedAt: IsoDateTimeSchema,
    grossWeightLb: nonNegativeNumberSchema.nullable(),
    tareWeightLb: nonNegativeNumberSchema.nullable(),
    netWeightLb: nonNegativeNumberSchema.nullable(),
    isComplete: z.boolean(),
    scaleId: z.uuid().nullable(),
    notes: z.string().trim().min(1).nullable(),
    photoUrl: z.string().trim().min(1).nullable(),
    createdAt: IsoDateTimeSchema,
    createdByUserId: z.uuid().nullable(),
    updatedAt: IsoDateTimeSchema,
    voidedAt: IsoDateTimeSchema.nullable(),
    voidedByUserId: z.uuid().nullable(),
    voidReason: z.string().trim().min(1).nullable(),
  })
  .strict()
  .superRefine(validateScaleTicketPayloadWeights);

export const ShipmentScaleTicketEventPayloadSchema: z.ZodType<ShipmentScaleTicketEventPayload> =
  ShipmentSyncPayloadBaseSchema.extend({
    actorUserId: z.uuid().nullable(),
    scaleTicket: ShipmentScaleTicketPayloadSchema,
  }).strict();

export const ShipmentWeighEventSyncPayloadSchema: z.ZodType<ShipmentWeighEventSyncPayload> = z
  .object({
    schemaVersion: z.literal(1),
    eventId: z.uuid(),
    occurredAt: IsoDateTimeSchema,
    companyId: z.uuid(),
    stationId: z.uuid(),
    shipmentId: z.uuid(),
    outboundRecordType: OutboundRecordTypeSchema,
    outboundRecordId: z.uuid(),
    actorUserId: z.uuid().nullable(),
    weighEvent: ShipmentWeighEventSchema,
  })
  .strict();

export const ShipmentDriverAssignmentSyncPayloadSchema: z.ZodType<ShipmentDriverAssignmentSyncPayload> =
  ShipmentSyncPayloadBaseSchema.extend({
    outboundRecordType: OutboundRecordTypeSchema,
    outboundRecordId: z.uuid(),
    actorUserId: z.uuid().nullable(),
    assignment: ShipmentDriverAssignmentSchema,
  }).strict();

export const ShipmentLogisticsShellUpsertedEventPayloadSchema: z.ZodType<ShipmentLogisticsShellUpsertedEventPayload> =
  z
    .object({
      ...ShipmentLogisticsShellPayloadBaseFields,
      actorUserId: z.uuid().nullable(),
      shipmentReference: z.string().trim().min(1),
      storeId: z.uuid(),
      locationId: z.uuid(),
      rcScopeType: RcScopeTypeSchema,
      rcScopeId: z.uuid(),
      materialId: z.uuid(),
      receiverLabel: z.string().trim().min(1).nullable(),
      departedAt: IsoDateTimeSchema,
      captureEligible: z.boolean(),
      secureLinkEligible: z.boolean(),
      activeDriverEmployeeIds: z.array(z.uuid()),
      materialRefs: z.array(ShipmentLogisticsCaptureShellMaterialRefSchema).nonempty(),
      sourceCreatedAt: IsoDateTimeSchema.nullable(),
      sourceUpdatedAt: IsoDateTimeSchema.nullable(),
    })
    .strict()
    .transform(normalizeShipmentLogisticsShellReference);

export const ShipmentLogisticsShellReversedEventPayloadSchema: z.ZodType<ShipmentLogisticsShellReversedEventPayload> =
  z
    .object({
      ...ShipmentLogisticsShellPayloadBaseFields,
      actorUserId: z.uuid().nullable(),
      reversedAt: IsoDateTimeSchema,
      reasonCode: z.string().trim().min(1).max(64).nullable(),
      reasonText: z.string().trim().min(1).nullable(),
    })
    .strict()
    .transform(normalizeShipmentLogisticsShellReference);
